import { DocumentType, DocumentsType, ScheduleCheckFileType } from '@consigli/types';
import clsx from 'clsx';
import { TFunction } from 'i18next';
import { Highlight } from 'react-highlighter-ts';
import { useTranslation } from 'react-i18next';

import { DownloadFileButton } from '@/components/download-file-button';
import { mapExtensionToUppercase } from '@/util/document-check';

import { DocumentCardParsedData } from './document-card-parsed-data';

interface DocumentCardProps {
  document: DocumentsType;
  selected: string;
  searchText?: string;
  showScheduleCheckFileType?: boolean;
}

const getScheduleCheckFileTypeBadge = (fileType: ScheduleCheckFileType, t: TFunction) => {
  switch (fileType) {
    case ScheduleCheckFileType.SCHEDULE_FILE:
      return {
        label: t('document-list.schedule-check-file-type.schedule-file'),
        color: 'bg-blue-100 text-blue-700',
      };
    case ScheduleCheckFileType.DRAWINGS:
      return {
        label: t('document-list.schedule-check-file-type.drawings'),
        color: 'bg-green-100 text-green-700',
      };
    case ScheduleCheckFileType.SCHEMATIC_DRAWINGS:
      return {
        label: t('document-list.schedule-check-file-type.schematic-drawings'),
        color: 'bg-purple-100 text-purple-700',
      };
    default:
      return null;
  }
};

export const DocumentCard = ({
  document,
  selected,
  searchText,
  showScheduleCheckFileType = false,
}: DocumentCardProps) => {
  const { t } = useTranslation();
  const scheduleCheckBadge =
    showScheduleCheckFileType && document.scheduleCheckFileType
      ? getScheduleCheckFileTypeBadge(document.scheduleCheckFileType, t)
      : null;

  return (
    <div className="grid grid-cols-5 gap-x-4 gap-y-2">
      <div className="col-span-4 flex flex-col truncate">
        <div className="flex-1 ">
          <div
            className={clsx(
              'text-day-neutral-subtle',
              selected === document.id && 'text-white',
              'text-xs mb-1',
            )}
          >
            {mapExtensionToUppercase(document.extension)}
          </div>
          <div className="flex items-center gap-2">
            <div
              className={clsx(
                selected === document.id ? 'text-white' : 'day-neutral-dark',
                'text-base font-semibold',
              )}
            >
              {/* @ts-expect-error - As event handlers which don't exist for React are required */}
              <Highlight placeholder="" search={searchText}>
                <p>{document.name}</p>
              </Highlight>
            </div>
            {scheduleCheckBadge && (
              <span
                className={clsx(
                  'px-2 py-0.5 rounded text-xs font-medium flex-shrink-0',
                  scheduleCheckBadge.color,
                )}
              >
                {scheduleCheckBadge.label}
              </span>
            )}
          </div>
        </div>
        {document.type === DocumentType.BLOB && (
          <DocumentCardParsedData
            initialParsedData={document.parsedData.data}
            remainingOccurrences={document.parsedData.remainingOccurrences}
            documentId={document.id}
            searchText={searchText}
            selected={selected === document.id}
          />
        )}
      </div>
      <div className="col-span-1 w-fit ml-auto flex items-center">
        <DownloadFileButton
          document={document}
          selected={selected === document.id}
          className="rounded-full"
        />
      </div>
    </div>
  );
};
